import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import LoginButton from '../components/LoginButton';
import FullPackCard from '../components/FullPackCard';
import StarterPackCard from '../components/StarterPackCard';
import PaymentMethodIcons from '../components/PaymentMethodIcons';
import CheckoutRecoveryModal from '../components/CheckoutRecoveryModal';
import ExpressCheckoutButton from '../components/ExpressCheckoutButton';
import api from '../lib/api';
import { trackEvent, trackOnVisible } from '../lib/tracker';

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeFAQ, setActiveFAQ] = useState(null);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [stats, setStats] = useState({ tracks: 1376, genres: 31, newWeek: 100, activeDjs: 100 });
  const [latestTracks, setLatestTracks] = useState([]);
  const [topArtists, setTopArtists] = useState([]);
  const [trendingTracks, setTrendingTracks] = useState([]);
  const [topTrack, setTopTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [hasPreviousSubscription, setHasPreviousSubscription] = useState(false);
  const audioRef = useRef(null);

  // Demo audio URL - will use real track from API
  const demoAudioUrl = null;

  useEffect(() => {
    // Track landing view ONCE on mount (anonymous, then stitched on signup)
    trackEvent('landing_view');

    // Returning from an abandoned Stripe checkout → retention modal (hottest recovery moment)
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout_cancelled') === 'true') {
      trackEvent('checkout_cancelled', {}, { allowDuplicate: true });
      setShowRecoveryModal(true);
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    }

    // IntersectionObserver: when user scrolls to the pricing section
    let cleanupObserver = () => {};
    const wireObserver = () => {
      const el = document.getElementById('pricing');
      if (el) cleanupObserver = trackOnVisible(el, 'pricing_view');
    };
    // Slight delay so the section is in the DOM
    const t = setTimeout(wireObserver, 600);

    const checkAuth = async () => {
      // Skip auto-redirect when returning from an abandoned checkout —
      // the retention modal must be shown even to authenticated users
      const cancelled = params.get('checkout_cancelled') === 'true';
      try {
        const response = await api.get('/auth/me');
        if (response.status === 200 && response.data) {
          // Capture prior-subscription flag so CTAs render correctly for returning users
          if (response.data.has_previous_subscription) {
            setHasPreviousSubscription(true);
          }
          if (!cancelled) {
            window.location.href = '/dashboard';
          }
        }
      } catch (error) {
        // 401 is expected when user is not logged in; log everything else
        if (error?.response?.status !== 401) {
          console.warn('[LandingPage] auth check failed:', error?.message || error);
        }
      } finally {
        setIsAuthChecking(false);
      }
    };
    
    const fetchPublicData = async () => {
      try {
        // Fetch latest tracks
        const tracksResponse = await api.get('/tracks/latest?limit=6');
        if (tracksResponse.data.tracks) {
          setLatestTracks(tracksResponse.data.tracks);
        }
        
        // Fetch public stats
        const statsResponse = await api.get('/stats/public');
        if (statsResponse.data) {
          setStats({
            tracks: statsResponse.data.tracks || 1376,
            genres: statsResponse.data.genres || 31,
            newWeek: Math.min(statsResponse.data.new_this_week || 100, 150), // Cap at reasonable number
            activeDjs: statsResponse.data.active_djs || 100
          });
        }
        
        // Fetch top artists for marquee
        const artistsResponse = await api.get('/artists/top?limit=15');
        if (artistsResponse.data.artists) {
          setTopArtists(artistsResponse.data.artists);
        }
        
        // Fetch trending/top downloaded for charts
        const trendingResponse = await api.get('/tracks/trending?limit=5');
        if (trendingResponse.data?.tracks) {
          setTrendingTracks(trendingResponse.data.tracks);
          // Set top track for demo player (most popular)
          if (trendingResponse.data.tracks.length > 0) {
            setTopTrack(trendingResponse.data.tracks[0]);
          }
        }
        
        // Fallback: if no trending, use first latest track for demo
        if (!trendingResponse.data?.tracks?.length && tracksResponse.data?.tracks?.length > 0) {
          setTopTrack(tracksResponse.data.tracks[0]);
        }
      } catch (error) {
        console.error('Error fetching public data:', error);
      }
    };
    
    checkAuth();
    fetchPublicData();

    return () => {
      clearTimeout(t);
      cleanupObserver();
    };
  }, []);

  // Reload audio when topTrack changes - CRITICAL for correct playback
  useEffect(() => {
    if (audioRef.current && topTrack?.file_id) {
      // Reset player state
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      
      // Build the correct audio URL - use PREVIEW endpoint for landing page (public, no auth required)
      const audioUrl = `${process.env.REACT_APP_BACKEND_URL}/api/preview/${topTrack.file_id}`;
      
      // Update the audio source and reload
      audioRef.current.src = audioUrl;
      audioRef.current.load();
      
      console.debug('[Demo Player] Loaded top track:', {
        title: topTrack.title,
        artist: topTrack.artist,
        file_id: topTrack.file_id,
        audioUrl: audioUrl
      });
    }
  }, [topTrack?.file_id]); // Only reload when file_id changes

  // Audio player functions
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().then(() => {
          setIsPlaying(true);
        }).catch(e => {
          console.error('[Demo Player] Play error:', e);
          setIsPlaying(false);
        });
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    if (audioRef.current) {
      audioRef.current.currentTime = percentage * duration;
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSubscribe = async () => {
    try {
      trackEvent('pricing_cta_click', {}, { allowDuplicate: true });
      setIsProcessing(true);
      
      // First check if user is authenticated AND get their subscription status
      let isAuthenticated = false;
      let currentUser = null;
      try {
        const authResponse = await api.get('/auth/me', { withCredentials: true });
        isAuthenticated = !!authResponse.data;
        currentUser = authResponse.data;
      } catch (e) {
        isAuthenticated = false;
      }

      // If already authenticated AND already subscribed → skip checkout entirely
      if (isAuthenticated && currentUser?.subscription_status === 'active') {
        setIsProcessing(false);
        window.location.href = '/dashboard';
        return;
      }

      if (!isAuthenticated) {
        // Redirect to login - after login they'll be redirected back
        // Store intent in localStorage so we can redirect to checkout after login
        localStorage.setItem('wtpl_intent', 'subscribe');
        const redirectUrl = window.location.origin + '/auth/callback';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
        return;
      }
      
      // User is authenticated but not active → proceed to checkout
      const response = await api.post('/subscriptions/create-checkout', {}, { 
        withCredentials: true,
        timeout: 30000
      });
      
      if (response.data.url) {
        setTimeout(() => { window.location.assign(response.data.url); }, 100);
      } else {
        throw new Error('No se recibió URL de pago');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setIsProcessing(false);
      alert('Error al procesar. Por favor intenta de nuevo.');
    }
  };

  const toggleFAQ = (index) => setActiveFAQ(activeFAQ === index ? null : index);

  // CTA copy adapts to whether the user has already used a trial before
  const ctaPrimary = hasPreviousSubscription ? 'Reactivar mi Suscripción' : 'Prueba 7 Días Gratis';
  const ctaSubText = hasPreviousSubscription
    ? 'Hoy $4.99 · Cancela cuando quieras'
    : 'Hoy pagas $0 · Luego $4.99/mes · Cancela cuando quieras';

  // Format version/type for display
  const getVersionTag = (track) => {
    if (track.version) return track.version;
    if (track.clean_version) return 'Clean';
    const folder = track.folder?.toLowerCase() || '';
    if (folder.includes('intro')) return 'Intro';
    if (folder.includes('mashup')) return 'Mashup';
    if (folder.includes('extended')) return 'Extended';
    if (folder.includes('edit')) return 'Edit';
    return 'Original';
  };

  if (isAuthChecking) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#00D4FF]"></div>
      </div>
    );
  }

  const faqs = [
    { question: "¿Qué formatos de audio tienen?", answer: "MP3 320kbps de alta calidad, listos para Serato, Rekordbox, Traktor y cualquier software DJ." },
    { question: "¿Cada cuánto agregan música?", answer: "Todos los viernes. 100-120 tracks nuevos por semana con los últimos lanzamientos y edits exclusivos." },
    { question: "¿Puedo pedir canciones específicas?", answer: "Sí, puedes sugerir tracks y los consideramos para las próximas actualizaciones." },
    { question: "¿Son edits o versiones originales?", answer: "Ambas. Incluimos hits originales + edits curados (intro extendido, clean versions, etc)." },
    { question: "¿Puedo cancelar cuando quiera?", answer: "Sí. Sin contrato, sin trampas. Cancelas cuando quieras desde tu cuenta." },
    { question: "¿Funciona con mi software DJ?", answer: "Sí. Compatible con Serato DJ, Rekordbox, Traktor, Virtual DJ y cualquier otro software." },
  ];

  const genres = [
    { name: 'Reggaetón', count: '250+', color: '#00D4FF', svgPath: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z', slug: 'reggaeton' },
    { name: 'Dembow', count: '150+', color: '#00D4FF', svgPath: 'M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3zM19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8', slug: 'dembow' },
    { name: 'Bachata', count: '100+', color: '#BDBDBD', svgPath: 'M9 18V5l12-2v13M9 19c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2zm12-3c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2z', slug: 'bachata' },
    { name: 'Guaracha', count: '80+', color: '#00D4FF', svgPath: 'M15.6 11.6L22 7v10l-6.4-4.5v-1zM4 5h9a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V7c0-1.1.9-2 2-2z', slug: 'guaracha' },
    { name: 'Latin Pop', count: '120+', color: '#00D9FF', svgPath: 'M9 18V5l12-2v13M9 19c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2zm12-3c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2z', slug: 'latin-pop' },
    { name: 'Salsa', count: '60+', color: '#FF0066', svgPath: 'M3 14h3a2 2 0 012 2v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-7a9 9 0 0118 0v7a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3', slug: 'salsa' },
    { name: 'Open Format', count: '200+', color: '#808080', svgPath: 'M4 14a1 1 0 01-.78-1.63l9.9-10.2a.5.5 0 01.86.46l-1.92 6.02A1 1 0 0013 10h7a1 1 0 01.78 1.63l-9.9 10.2a.5.5 0 01-.86-.46l1.92-6.02A1 1 0 0011 14z', slug: 'open-format' },
    { name: 'Latin Edits', count: '180+', color: '#06B6D4', svgPath: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z', slug: 'latin-edits' },
  ];

  // Split artists into trending and top downloaded
  const trendingArtists = topArtists.filter(a => a.trending);
  const topDownloadedArtists = topArtists.filter(a => a.top_rated || a.download_count > 0);

  return (
    <div className="bg-[#000000] text-white min-h-screen">
      {/* Header - Fixed Navigation */}
      <motion.header 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 bg-[#000000]/95 backdrop-blur-lg border-b border-[#1A1A1A]"
      >
        <div className="max-w-[1440px] mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-icon.png" alt="WTPL" className="h-8 w-8" />
            <span className="font-bold text-lg tracking-wider" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>WTPL</span>
            <span className="text-[#333] mx-1 hidden sm:block">|</span>
            <span className="text-[11px] text-[#808080] font-medium hidden sm:block tracking-[0.2em] uppercase">DJ Pool</span>
          </div>
          
          <nav className="hidden lg:flex items-center gap-8">
            {[
              { name: 'Inicio', href: '#' },
              { name: 'Biblioteca', href: '#biblioteca' },
              { name: 'Géneros', href: '#generos' },
              { name: 'Planes', href: '#pricing' },
              { name: 'FAQ', href: '#faq' }
            ].map((item) => (
              <a 
                key={item.name}
                href={item.href}
                className="text-xs text-[#808080] hover:text-[#00D4FF] transition-colors tracking-[0.15em] uppercase font-medium"
              >
                {item.name}
              </a>
            ))}
          </nav>
          
          <div className="flex items-center gap-3">
            <LoginButton variant="outline" className="text-sm hidden md:flex" />
          </div>
        </div>
      </motion.header>

      {/* Dynamic Artist Marquee - Compact */}
      <div className="pt-16">
        <div className="bg-[#000000] border-b border-[#1A1A1A] py-2.5 overflow-hidden">
          {/* Single Row Marquee - Compact */}
          <Marquee speed={35} gradient={true} gradientColor="#000000" gradientWidth={60}>
            {(trendingArtists.length > 0 ? trendingArtists : topArtists.slice(0, 10)).concat(topDownloadedArtists.length > 0 ? topDownloadedArtists : topArtists.slice(0, 10)).map((artist, i) => (
              <span 
                key={`artist-${i}`}
                className="mx-4 text-xs cursor-pointer hover:text-[#00D4FF] transition-all flex items-center gap-1.5 text-[#888]"
              >
                <span className="text-[#00D4FF]">★</span>
                <span>{artist.name}</span>
              </span>
            ))}
          </Marquee>
        </div>
      </div>

      {/* Hero Section - Desktop First with Blue Accent */}
      <section className="pt-16 pb-20 px-4 lg:px-8 overflow-hidden relative">
        {/* Cyan glow background - DJ LYO Brand */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#00D4FF]/8 via-transparent to-transparent pointer-events-none"></div>
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#00D4FF]/6 rounded-full blur-[150px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#000000] to-transparent pointer-events-none"></div>
        
        <div className="max-w-[1440px] mx-auto relative z-10">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
            {/* Left Column - Main Content (3/5) */}
            <div className="lg:col-span-3">
              {/* Trust Badges Row - Blue Theme */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-wrap items-center gap-3 mb-6"
              >
                <div className="flex items-center gap-2 bg-transparent border border-[#333] rounded-full px-4 py-2">
                  <svg className="w-3.5 h-3.5 text-[#888]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13M9 19c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2zm12-3c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2z"/><path d="M9 10l12-3"/></svg>
                  <span className="text-sm font-bold text-white">{stats.tracks.toLocaleString()}</span>
                  <span className="text-xs text-[#888]">Tracks</span>
                </div>
                <div className="flex items-center gap-2 bg-transparent border border-[#333] rounded-full px-4 py-2">
                  <svg className="w-3.5 h-3.5 text-[#888]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                  <span className="text-sm font-bold text-white">100+</span>
                  <span className="text-xs text-[#888]">nuevos/semana</span>
                </div>
                <div className="flex items-center gap-2 bg-transparent border border-[#333] rounded-full px-4 py-2">
                  <svg className="w-3.5 h-3.5 text-[#888]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M3 14h3a2 2 0 012 2v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-7a9 9 0 0118 0v7a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3"/></svg>
                  <span className="text-sm font-bold text-white">{stats.genres}</span>
                  <span className="text-xs text-[#888]">géneros</span>
                </div>
              </motion.div>
              
              {/* Main Headline - LARGER */}
              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-[1.05] tracking-tight" 
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                <span className="bg-gradient-to-r from-white via-white to-[#B3B3B3] bg-clip-text text-transparent">EL DJ POOL</span><br />
                <span className="bg-gradient-to-r from-[#00D4FF] via-[#00D4FF] to-[#FFFFFF] bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(0,212,255,0.4)]">LATINO #1</span>
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-lg lg:text-xl text-[#B3B3B3] mb-8 max-w-xl leading-relaxed"
              >
                Música latina curada para DJs profesionales. Reggaetón, Dembow, Bachata, Guaracha y más. 
                <span className="text-white font-medium"> Descarga ilimitada, calidad profesional.</span>
              </motion.p>
              
              {/* Primary CTA Buttons - IMPROVED */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-wrap items-center gap-4 mb-6"
              >
                <motion.button
                  onClick={handleSubscribe}
                  whileHover={{ scale: 1.03, boxShadow: '0 0 30px rgba(0,212,255,0.3)' }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-[#00D4FF] hover:bg-[#00D4FF]/90 text-black font-bold px-8 py-3.5 rounded-full text-sm tracking-[0.1em] uppercase flex items-center gap-2 shadow-lg shadow-[#00D4FF]/20 transition-all duration-300"
                  data-testid="hero-cta-btn"
                >
                  {ctaPrimary}
                  <span className="text-sm">→</span>
                </motion.button>
                <motion.a 
                  href="#pricing"
                  whileHover={{ scale: 1.03, borderColor: '#555' }}
                  whileTap={{ scale: 0.98 }}
                  className="border border-[#333] text-white font-medium px-8 py-3.5 rounded-full transition-all duration-300 hover:bg-white/5 text-sm tracking-[0.1em] uppercase"
                >
                  Ver Planes
                </motion.a>
              </motion.div>
              
              {/* Payment Methods Trust Badges */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="mb-6"
              >
                <PaymentMethodIcons size="sm" className="!items-start" showPayPal={false} />
              </motion.div>
              
              {/* Quick Benefits - NEW */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="grid grid-cols-2 gap-2 mb-6"
              >
                {[
                  { text: 'Descargas ilimitadas' },
                  { text: 'MP3 320kbps' },
                  { text: 'BPM y Key analizados' },
                  { text: 'Nuevos edits cada semana' },
                ].map((benefit, i) => (
                  <div key={benefit.text} className="flex items-center gap-2 text-sm">
                    <svg className="w-4 h-4 text-[#00D4FF] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    <span className="text-[#B3B3B3]">{benefit.text}</span>
                  </div>
                ))}
              </motion.div>
              
              <p className="text-sm text-[#666]">
                {hasPreviousSubscription ? (
                  <>
                    <span className="text-[#00D4FF] font-semibold">Reactivar suscripción</span> • Hoy $4.99 • Cancela cuando quieras
                  </>
                ) : (
                  <>
                    <span className="text-[#00D4FF] font-semibold">7 días gratis</span> • Luego $4.99/mes • Cancela cuando quieras
                  </>
                )}
              </p>

              {/* Functional Demo Player - Uses Top Track */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-8 bg-gradient-to-br from-[#141414] to-[#0D0D0D] border border-[#262626] rounded-2xl p-5 shadow-xl"
              >
                {/* Hidden Audio Element - Uses real track if available */}
                {/* Key forces re-mount when track changes to ensure correct audio loads */}
                <audio
                  key={topTrack?.file_id || 'demo'}
                  ref={audioRef}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={() => setIsPlaying(false)}
                  onError={(e) => console.error('[Demo Player] Audio error:', e)}
                  preload="metadata"
                >
                  {topTrack?.file_id && (
                    <source 
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/preview/${topTrack.file_id}`}
                      type="audio/mpeg"
                    />
                  )}
                </audio>
                
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-[#888]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M3 14h3a2 2 0 012 2v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-7a9 9 0 0118 0v7a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3"/></svg>
                    <span className="text-sm font-medium text-white">
                      {topTrack ? 'Track más popular del pool' : 'Escucha cómo suenan nuestros edits'}
                    </span>
                  </div>
                  <span className="text-xs bg-gradient-to-r from-[#00D4FF]/20 to-[#00D4FF]/20 text-[#00D4FF] px-3 py-1 rounded-full font-bold border border-[#00D4FF]/30">
                    {topTrack ? '#1 TOP' : 'DEMO'}
                  </span>
                </div>
                
                <div className="flex items-center gap-4">
                  {/* Play/Pause Button */}
                  <motion.button
                    onClick={togglePlay}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0 ${
                      isPlaying 
                        ? 'bg-gradient-to-br from-[#00D4FF] to-[#00B8FF] text-black shadow-lg shadow-[#00D4FF]/30' 
                        : 'bg-[#262626] text-[#00D4FF] hover:bg-[#333] hover:shadow-lg hover:shadow-[#00D4FF]/20'
                    }`}
                  >
                    {isPlaying ? (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 4h4v16H6zM14 4h4v16h-4z"/>
                      </svg>
                    ) : (
                      <svg className="w-6 h-6 ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    )}
                  </motion.button>
                  
                  <div className="flex-1 min-w-0">
                    {/* Track Info - Shows real track if available */}
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {topTrack ? topTrack.title : 'Mix Demo - Latin Hits 2026'}
                        </p>
                        {topTrack && (
                          <p className="text-xs text-[#888] truncate">{topTrack.artist}</p>
                        )}
                      </div>
                      {isPlaying && (
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <span className="w-1 h-3 bg-[#00D4FF] rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-1 h-4 bg-[#00D4FF] rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-1 h-2 bg-[#00D4FF] rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></span>
                          <span className="w-1 h-3 bg-[#00D4FF] rounded-full animate-pulse" style={{ animationDelay: '450ms' }}></span>
                        </div>
                      )}
                      {topTrack?.bpm && (
                        <span className="text-xs text-[#00D4FF] font-mono font-bold flex-shrink-0">{topTrack.bpm} BPM</span>
                      )}
                    </div>
                    
                    {/* Progress Bar - Clickable */}
                    <div 
                      className="h-2 bg-[#262626] rounded-full overflow-hidden cursor-pointer group"
                      onClick={handleSeek}
                    >
                      <div 
                        className="h-full bg-gradient-to-r from-[#00D4FF] to-[#00B8FF] rounded-full transition-all duration-100 relative"
                        style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
                      >
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      </div>
                    </div>
                    
                    {/* Time Display */}
                    <div className="flex justify-between mt-2">
                      <span className="text-xs text-[#888] font-mono">{formatTime(currentTime)}</span>
                      <span className="text-xs text-[#888] font-mono">{formatTime(duration)}</span>
                    </div>
                  </div>
                  
                  {/* Volume Control */}
                  <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                    <svg className="w-4 h-4 text-[#666]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
                    </svg>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={volume}
                      onChange={handleVolumeChange}
                      className="w-20 h-1 bg-[#262626] rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-[#00D4FF] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer"
                    />
                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* Right Column - Latest Tracks Card (2/5) - IMPROVED */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="lg:col-span-2"
            >
              <div className="bg-gradient-to-br from-[#141414] to-[#0D0D0D] border border-[#262626] rounded-2xl shadow-2xl overflow-hidden">
                {/* Card Header - Clean Design */}
                <div className="px-5 py-4 border-b border-[#1A1A1A] bg-[#0D0D0D]/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="font-bold text-white text-lg">Últimos Agregados</h3>
                      <span className="text-xs bg-[#00D4FF]/10 text-[#00D4FF] px-3 py-1 rounded-full font-semibold border border-[#00D4FF]/20">
                        Nuevo
                      </span>
                    </div>
                    <span className="text-xs text-[#666]">Esta semana</span>
                  </div>
                </div>
                
                {/* Tracks List - Premium Table Style */}
                <div className="p-2">
                  {latestTracks.length > 0 ? (
                    latestTracks.slice(0, 6).map((track, i) => (
                      <motion.div 
                        key={track.id || track.file_id || `latest-${i}`}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + (i * 0.05) }}
                        whileHover={{ backgroundColor: 'rgba(0,217,255,0.08)' }}
                        className={`flex items-center gap-4 py-3.5 px-3 cursor-pointer group transition-all duration-200 rounded-xl ${
                          i < latestTracks.length - 1 ? 'border-b border-[#1A1A1A]/50' : ''
                        }`}
                      >
                        {/* Track Number */}
                        <span className="w-5 text-center text-xs font-bold text-[#444] group-hover:text-[#00D4FF] transition-colors">
                          {i + 1}
                        </span>
                        
                        {/* Mini Album Art Placeholder */}
                        <div className="w-11 h-11 bg-gradient-to-br from-[#262626] to-[#1A1A1A] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:from-[#00D4FF]/20 group-hover:to-[#00D4FF]/10 transition-all shadow-inner">
                          <svg className="w-4 h-4 text-[#00D4FF] group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                        
                        {/* Track Info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-white text-sm truncate group-hover:text-[#00D4FF] transition-colors">{track.title}</p>
                          <p className="text-xs text-[#666] truncate mt-0.5">{track.artist}</p>
                        </div>
                        
                        {/* BPM | Key | Version - Better Spacing */}
                        <div className="text-right flex-shrink-0 hidden sm:flex items-center gap-3">
                          <div className="text-center">
                            <span className="text-xs text-[#00D4FF] font-mono font-bold block">{track.bpm || '---'}</span>
                            <span className="text-[9px] text-[#555]">BPM</span>
                          </div>
                          <div className="w-px h-8 bg-[#262626]"></div>
                          <div className="text-center">
                            <span className="text-xs text-[#BDBDBD] font-mono font-bold block">{track.key || track.music_key || '---'}</span>
                            <span className="text-[9px] text-[#555]">KEY</span>
                          </div>
                          <div className="w-px h-8 bg-[#262626]"></div>
                          <div className="text-center min-w-[50px]">
                            <span className="text-[10px] px-2 py-1 bg-[#262626] text-[#888] rounded-md group-hover:bg-[#00D4FF]/20 group-hover:text-[#00D4FF] transition-colors inline-block font-medium">
                              {getVersionTag(track)}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    Array(6).fill(0).map((_, i) => (
                      <div key={`top-skeleton-${i}`} className="flex items-center gap-4 py-3.5 px-3 animate-pulse">
                        <span className="w-5 h-4 bg-[#262626] rounded"></span>
                        <div className="w-11 h-11 bg-[#262626] rounded-lg"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-[#262626] rounded w-3/4 mb-1.5"></div>
                          <div className="h-3 bg-[#262626] rounded w-1/2"></div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                {/* Card Footer */}
                <div className="px-5 py-4 border-t border-[#1A1A1A] bg-[#0D0D0D]/50">
                  <motion.a
                    href="#biblioteca"
                    whileHover={{ scale: 1.02, backgroundColor: '#1A1A1A' }}
                    className="w-full py-3 bg-[#141414] text-center rounded-xl text-sm text-[#888] hover:text-white transition-all flex items-center justify-center gap-2 border border-[#262626]"
                  >
                    Ver toda la librería
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                    </svg>
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Charts Section - PREMIUM REDESIGN with Ambient Glow */}
      <section className="py-24 px-4 lg:px-8 relative overflow-hidden">
        {/* Ambient Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#000000] via-[#0D1218] to-[#000000]"></div>
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#00D4FF]/5 rounded-full blur-[150px] pointer-events-none"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#00D4FF]/5 rounded-full blur-[120px] pointer-events-none"></div>
        
        <div className="max-w-[1440px] mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-transparent border border-[#333] rounded-full mb-6">
              <svg className="w-4 h-4 text-[#888]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/><path d="M12 3v12"/><path d="M5 21h14"/></svg>
              <span className="text-sm font-semibold text-[#B3B3B3]">Rankings en vivo</span>
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold mb-4 uppercase tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <span className="text-white">Charts de la</span>{' '}
              <span className="bg-gradient-to-r from-[#00D4FF] via-[#00D4FF] to-[#FFFFFF] bg-clip-text text-transparent">Semana</span>
            </h2>
            <p className="text-[#888] text-lg">Descubre lo más popular en WTPL</p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Trending This Week - Premium Card */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -5, boxShadow: '0 25px 50px rgba(255,0,245,0.15)' }}
              className="bg-gradient-to-br from-[#141418] via-[#1A1A22] to-[#141418] border border-[#00D4FF]/20 rounded-2xl p-6 relative overflow-hidden transition-all duration-500"
            >
              {/* Card Glow */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#00D4FF] to-transparent"></div>
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#00D4FF]/10 rounded-full blur-[60px]"></div>
              
              <h3 className="font-bold text-[#00D4FF] mb-5 flex items-center gap-2 text-lg relative z-10">
                <span className="w-8 h-8 flex items-center justify-center bg-[#00D4FF]/20 rounded-lg">
                  <svg className="w-4 h-4 text-[#00D4FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                </span>
                Trending This Week
              </h3>
              <div className="space-y-2 relative z-10">
                {(trendingTracks.length > 0 ? trendingTracks : latestTracks).slice(0, 5).map((track, i) => (
                  <motion.div 
                    key={track.id || track.file_id || `trending-${i}`} 
                    whileHover={{ x: 4, backgroundColor: 'rgba(255,0,245,0.1)' }}
                    className="flex items-center gap-3 py-3 px-3 rounded-xl cursor-pointer transition-all border border-transparent hover:border-[#00D4FF]/20"
                  >
                    <span className="text-sm w-8 text-center font-black">
                      {i < 3 ? <span className={`${i === 0 ? 'text-[#00D9FF]' : i === 1 ? 'text-[#B3B3B3]' : 'text-[#666]'}`}>{i + 1}</span> : <span className="text-[#555]">{i + 1}</span>}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate text-white">{track.title}</p>
                      <p className="text-xs text-[#666] truncate">{track.artist}</p>
                    </div>
                    <span className="text-xs text-[#00D4FF] font-mono font-bold bg-[#00D4FF]/10 px-2 py-1 rounded">{track.bpm}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            {/* Top Downloads - Premium Card */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              whileHover={{ y: -5, boxShadow: '0 25px 50px rgba(0,255,136,0.15)' }}
              className="bg-gradient-to-br from-[#141418] via-[#1A1A22] to-[#141418] border border-[#00D4FF]/20 rounded-2xl p-6 relative overflow-hidden transition-all duration-500"
            >
              {/* Card Glow */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#00D4FF] to-transparent"></div>
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#00D4FF]/10 rounded-full blur-[60px]"></div>
              
              <h3 className="font-bold text-[#00D4FF] mb-5 flex items-center gap-2 text-lg relative z-10">
                <span className="w-8 h-8 flex items-center justify-center bg-[#00D4FF]/20 rounded-lg">
                  <svg className="w-4 h-4 text-[#00D4FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
                </span>
                Top Downloads
              </h3>
              <div className="space-y-2 relative z-10">
                {latestTracks.slice(0, 5).map((track, i) => (
                  <motion.div 
                    key={track.id || track.file_id || `top5-${i}`} 
                    whileHover={{ x: 4, backgroundColor: 'rgba(0,255,136,0.1)' }}
                    className="flex items-center gap-3 py-3 px-3 rounded-xl cursor-pointer transition-all border border-transparent hover:border-[#00D4FF]/20"
                  >
                    <span className="text-sm w-8 text-center font-black">
                      {i < 3 ? <span className={`${i === 0 ? 'text-[#00D9FF]' : i === 1 ? 'text-[#B3B3B3]' : 'text-[#666]'}`}>{i + 1}</span> : <span className="text-[#555]">{i + 1}</span>}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate text-white">{track.title}</p>
                      <p className="text-xs text-[#666] truncate">{track.artist}</p>
                    </div>
                    <span className="text-xs text-[#BDBDBD] font-mono font-bold bg-[#BDBDBD]/10 px-2 py-1 rounded">{track.key || track.music_key || '---'}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            {/* Club Hits - Premium Card */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              whileHover={{ y: -5, boxShadow: '0 25px 50px rgba(0,217,255,0.15)' }}
              className="bg-gradient-to-br from-[#141418] via-[#1A1A22] to-[#141418] border border-[#00D4FF]/20 rounded-2xl p-6 relative overflow-hidden transition-all duration-500"
            >
              {/* Card Glow */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#00D4FF] to-transparent"></div>
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#00D4FF]/10 rounded-full blur-[60px]"></div>
              
              <h3 className="font-bold text-[#00D4FF] mb-5 flex items-center gap-2 text-lg relative z-10">
                <span className="w-8 h-8 flex items-center justify-center bg-[#00D4FF]/20 rounded-lg">
                  <svg className="w-4 h-4 text-[#00D4FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 18V5l12-2v13M9 19c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2zm12-3c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2z"/></svg>
                </span>
                Club Hits
              </h3>
              <div className="space-y-2 relative z-10">
                {latestTracks.filter(t => t.bpm && parseInt(t.bpm) >= 100).slice(0, 5).map((track, i) => (
                  <motion.div 
                    key={track.id || track.file_id || `club-${i}`} 
                    whileHover={{ x: 4, backgroundColor: 'rgba(0,217,255,0.1)' }}
                    className="flex items-center gap-3 py-3 px-3 rounded-xl cursor-pointer transition-all border border-transparent hover:border-[#00D4FF]/20"
                  >
                    <span className="text-xl w-8 text-center font-black">
                      {i < 3 ? <span className={`${i === 0 ? 'text-[#00D9FF]' : i === 1 ? 'text-[#B3B3B3]' : 'text-[#666]'}`}>{i + 1}</span> : <span className="text-[#555]">{i + 1}</span>}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate text-white">{track.title}</p>
                      <p className="text-xs text-[#666] truncate">{track.artist}</p>
                    </div>
                    <span className="text-xs text-[#00D4FF] font-mono font-bold bg-[#00D4FF]/10 px-2 py-1 rounded">{getVersionTag(track)}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Genres Section - PREMIUM REDESIGN */}
      <section id="generos" className="py-24 px-4 lg:px-8 relative overflow-hidden">
        {/* Ambient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#000000] via-[#0D1015] to-[#000000]"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00D4FF]/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00D4FF]/30 to-transparent"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#00D4FF]/5 rounded-full blur-[150px] pointer-events-none"></div>
        
        <div className="max-w-[1440px] mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-transparent border border-[#333] rounded-full mb-6">
              <svg className="w-4 h-4 text-[#888]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 18V5l12-2v13M9 19c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2zm12-3c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2z"/></svg>
              <span className="text-sm font-semibold text-[#B3B3B3]">Géneros disponibles</span>
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold mb-4 uppercase tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <span className="text-white">Todos los Géneros</span>{' '}
              <span className="bg-gradient-to-r from-[#00D4FF] via-[#00D4FF] to-[#FFFFFF] bg-clip-text text-transparent">que Necesitas</span>
            </h2>
            <p className="text-[#888] text-lg">Música latina curada para cada momento de tu set</p>
          </motion.div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
            {genres.map((genre, i) => (
              <motion.a 
                key={genre.slug || genre.name || `genre-${i}`}
                href={`/genero/${genre.slug}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.03 }}
                whileHover={{ 
                  scale: 1.08, 
                  y: -8,
                }}
                className="group relative bg-gradient-to-br from-[#141418] to-[#0D0D12] border border-[#1A1A22] rounded-xl p-5 text-center cursor-pointer transition-all duration-500 overflow-hidden"
                style={{ 
                  '--genre-color': genre.color,
                }}
              >
                {/* Hover Glow Effect */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ 
                    background: `radial-gradient(circle at center, ${genre.color}15 0%, transparent 70%)`,
                    boxShadow: `inset 0 0 30px ${genre.color}10`
                  }}
                ></div>
                <div 
                  className="absolute inset-0 border border-transparent rounded-xl group-hover:border-[color:var(--genre-color)] transition-colors duration-300 opacity-50"
                  style={{ borderColor: 'transparent' }}
                ></div>
                
                <motion.div 
                  className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center relative z-10 transition-all duration-300"
                  style={{ 
                    backgroundColor: `${genre.color}15`,
                    boxShadow: `0 0 20px ${genre.color}00`
                  }}
                  whileHover={{ 
                    scale: 1.15, 
                    rotate: 5,
                    boxShadow: `0 0 30px ${genre.color}30`
                  }}
                >
                  <svg className="w-6 h-6" style={{ color: genre.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d={genre.svgPath}/></svg>
                </motion.div>
                <h3 className="font-bold text-white text-sm mb-1 group-hover:text-[color:var(--genre-color)] transition-colors relative z-10" style={{ '--genre-color': genre.color }}>
                  {genre.name}
                </h3>
                <p className="text-xs font-bold transition-colors relative z-10" style={{ color: genre.color }}>{genre.count}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Inside the DJ Pool - PREMIUM REDESIGN */}
      <section className="py-24 px-4 lg:px-8 relative overflow-hidden">
        {/* Ambient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#000000] via-[#0D1218] to-[#000000]"></div>
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-[#00D4FF]/8 rounded-full blur-[150px] pointer-events-none"></div>
        <div className="absolute bottom-1/3 left-0 w-[400px] h-[400px] bg-[#00D4FF]/5 rounded-full blur-[120px] pointer-events-none"></div>
        
        <div className="max-w-[1440px] mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-transparent border border-[#333] rounded-full mb-6">
              <svg className="w-4 h-4 text-[#888]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37zM17.5 6.5h.01"/></svg>
              <span className="text-sm font-semibold text-[#B3B3B3]">Vista previa del producto</span>
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold mb-4 uppercase tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <span className="text-white">Inside the</span>{' '}
              <span className="bg-gradient-to-r from-[#00D4FF] via-[#00D4FF] to-[#FFFFFF] bg-clip-text text-transparent">DJ Pool</span>
            </h2>
            <p className="text-[#888] text-lg max-w-2xl mx-auto">
              Así es como los DJs navegan y descargan música dentro de WTPL. 
              Interfaz profesional diseñada para flujo de trabajo rápido.
            </p>
          </motion.div>
          
          {/* Dashboard Preview - Premium Container */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#141418] via-[#1A1A22] to-[#141418] border border-[#00D4FF]/20 rounded-3xl p-2 relative overflow-hidden"
          >
            {/* Glow Effect */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#00D4FF] to-transparent"></div>
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[200px] bg-[#00D4FF]/10 rounded-full blur-[100px] pointer-events-none"></div>
            
            <div className="bg-[#0A0A0E] rounded-2xl p-6 lg:p-8 relative">
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Sidebar Preview */}
                <div className="bg-gradient-to-b from-[#141418] to-[#0D0D12] rounded-xl p-4 border border-[#1A1A22]">
                  <h4 className="text-sm font-bold text-[#00D4FF] mb-4 flex items-center gap-2">
                    <span className="w-6 h-6 flex items-center justify-center bg-[#00D4FF]/20 rounded">
                      <svg className="w-3.5 h-3.5 text-[#00D4FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
                    </span>
                    Carpetas
                  </h4>
                  <div className="space-y-1">
                    {['NEW MUSIC', 'LATIN SPOTLIGHT', 'OPEN FORMAT', 'DJ TOOLS', 'CUBAN REMIX'].map((folder, i) => (
                      <motion.div 
                        key={folder} 
                        whileHover={{ x: 4, backgroundColor: 'rgba(0,217,255,0.1)' }}
                        className="flex items-center gap-3 py-2.5 px-3 rounded-lg cursor-pointer transition-all border border-transparent hover:border-[#00D4FF]/20"
                      >
                        <svg className="w-4 h-4 text-[#00D4FF]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
                        <span className="text-sm font-medium">{folder}</span>
                        <span className="ml-auto text-xs text-[#555] bg-[#1A1A22] px-2 py-0.5 rounded">{Math.floor(Math.random() * 100) + 50}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
                
                {/* Track List Preview */}
                <div className="lg:col-span-2 bg-gradient-to-b from-[#141418] to-[#0D0D12] rounded-xl p-4 border border-[#1A1A22]">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-bold text-[#00D4FF] flex items-center gap-2">
                      <span className="w-6 h-6 flex items-center justify-center bg-[#00D4FF]/20 rounded">
                        <svg className="w-3.5 h-3.5 text-[#00D4FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 18V5l12-2v13M9 19c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2zm12-3c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2z"/></svg>
                      </span>
                      Tracks
                    </h4>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-[#00D4FF]/10 text-[#00D4FF] px-3 py-1.5 rounded-lg border border-[#00D4FF]/20">BPM</span>
                      <span className="text-xs bg-[#BDBDBD]/10 text-[#BDBDBD] px-3 py-1.5 rounded-lg border border-[#BDBDBD]/20">Key</span>
                      <span className="text-xs bg-[#1A1A22] text-[#888] px-3 py-1.5 rounded-lg border border-[#262626]">Filtros</span>
                    </div>
                  </div>
                  
                  {/* Sample Track Rows */}
                  <div className="space-y-2">
                    {[
                      { title: 'Gasolina (Intro Edit)', artist: 'Daddy Yankee', bpm: '100', key: '5A' },
                      { title: 'Titi Me Pregunto', artist: 'Bad Bunny', bpm: '110', key: '7B' },
                      { title: 'La Bachata', artist: 'Manuel Turizo', bpm: '128', key: '8A' },
                      { title: 'Provenza (Extended)', artist: 'Karol G', bpm: '98', key: '6A' },
                    ].map((track, i) => (
                      <motion.div 
                        key={track.title} 
                        whileHover={{ backgroundColor: 'rgba(0,217,255,0.08)' }}
                        className="flex items-center gap-4 py-3 px-4 bg-[#0D0D12] rounded-xl border border-[#1A1A22] hover:border-[#00D4FF]/30 transition-all"
                      >
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00D4FF]/20 to-[#00D4FF]/5 rounded-lg flex items-center justify-center text-[#00D4FF] hover:from-[#00D4FF]/30 transition-all cursor-pointer">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white truncate">{track.title}</p>
                          <p className="text-xs text-[#666] truncate">{track.artist}</p>
                        </div>
                        <span className="text-xs text-[#00D4FF] font-mono font-bold bg-[#00D4FF]/10 px-2 py-1 rounded">{track.bpm}</span>
                        <span className="text-xs text-[#BDBDBD] font-mono font-bold bg-[#BDBDBD]/10 px-2 py-1 rounded">{track.key}</span>
                        <motion.button 
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          className="px-4 py-2 bg-[#00D4FF] text-black text-xs font-bold rounded-full hover:shadow-lg hover:shadow-[#00D4FF]/30 transition-all"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
                        </motion.button>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Feature highlights */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t border-[#1A1A22]">
                {[
                  { icon: 'search', label: 'Búsqueda avanzada', color: '#00D4FF' },
                  { icon: 'key', label: 'Filtro por Key', color: '#BDBDBD' },
                  { icon: 'chart', label: 'Orden por BPM', color: '#00D4FF' },
                  { icon: 'download', label: 'Descarga en 1 click', color: '#00D4FF' },
                ].map((feature, i) => (
                  <motion.div 
                    key={feature.label} 
                    whileHover={{ y: -3 }}
                    className="text-center p-4 bg-[#0D0D12] rounded-xl border border-[#1A1A22] hover:border-[color:var(--feature-color)]/30 transition-all"
                    style={{ '--feature-color': feature.color }}
                  >
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg mb-2" style={{ backgroundColor: `${feature.color}15` }}>
                      {feature.icon === 'search' && <svg className="w-5 h-5" style={{color: feature.color}} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>}
                      {feature.icon === 'key' && <svg className="w-5 h-5" style={{color: feature.color}} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>}
                      {feature.icon === 'chart' && <svg className="w-5 h-5" style={{color: feature.color}} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M3 3v18h18"/><path d="M18 17V9M13 17V5M8 17v-3"/></svg>}
                      {feature.icon === 'download' && <svg className="w-5 h-5" style={{color: feature.color}} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>}
                    </div>
                    <p className="text-sm text-[#888] font-medium">{feature.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section - PREMIUM REDESIGN */}
      <section id="biblioteca" className="py-24 px-4 lg:px-8 relative overflow-hidden">
        {/* Ambient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#000000] via-[#0D1015] to-[#000000]"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00D4FF]/20 to-transparent"></div>
        <div className="absolute top-1/2 left-1/4 w-[500px] h-[500px] bg-[#00D4FF]/5 rounded-full blur-[150px] pointer-events-none"></div>
        
        <div className="max-w-[1440px] mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-4 uppercase tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <span className="text-white">Todo lo que un</span>{' '}
              <span className="bg-gradient-to-r from-[#00D4FF] to-[#00B8FF] bg-clip-text text-transparent">DJ Profesional</span>{' '}
              <span className="text-white">Necesita</span>
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
            {[
              { svgPath: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z', title: '100+/semana', desc: 'Nuevos tracks', highlight: true, color: '#00D4FF' },
              { svgPath: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3', title: 'Ilimitado', desc: 'Descargas', color: '#00D4FF' },
              { svgPath: 'M9 18V5l12-2v13M9 19c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2zm12-3c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2z', title: '320kbps', desc: 'MP3 HQ', color: '#BDBDBD' },
              { svgPath: 'M15.6 11.6L22 7v10l-6.4-4.5v-1zM4 5h9a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V7c0-1.1.9-2 2-2z', title: 'Camelot', desc: 'Keys', color: '#00D4FF' },
              { svgPath: 'M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z', title: 'Crates', desc: 'Organizados', color: '#00D4FF' },
              { svgPath: 'M4 14a1 1 0 01-.78-1.63l9.9-10.2a.5.5 0 01.86.46l-1.92 6.02A1 1 0 0013 10h7a1 1 0 01.78 1.63l-9.9 10.2a.5.5 0 01-.86-.46l1.92-6.02A1 1 0 0011 14z', title: 'Versiones', desc: 'Múltiples', color: '#00D4FF' },
              { svgPath: 'M11 19c-3.9 0-7-3.1-7-7s3.1-7 7-7 7 3.1 7 7-3.1 7-7 7zm0-12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zM21 21l-4.35-4.35', title: 'Búsqueda', desc: 'Avanzada', color: '#BDBDBD' },
              { svgPath: 'M12 2a10 10 0 1010 10A10 10 0 0012 2zm0 18a8 8 0 118-8 8 8 0 01-8 8zm3.5-9H13V8.5a1 1 0 00-2 0V11H8.5a1 1 0 000 2H11v2.5a1 1 0 002 0V13h2.5a1 1 0 000-2z', title: 'Acceso', desc: 'Web 24/7', color: '#00D4FF' },
            ].map((feature, i) => (
              <motion.div 
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.03 }}
                whileHover={{ y: -5, borderColor: feature.color }}
                className={`bg-gradient-to-br from-[#141418] to-[#0D0D12] border rounded-xl p-5 text-center transition-all duration-300 relative overflow-hidden ${
                  feature.highlight ? 'border-[#00D4FF]/40' : 'border-[#1A1A22]'
                }`}
              >
                {feature.highlight && (
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#00D4FF] to-transparent"></div>
                )}
                <div 
                  className="w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${feature.color}15` }}
                >
                  <svg className="w-5 h-5" style={{ color: feature.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d={feature.svgPath}/></svg>
                </div>
                <h3 className="font-bold text-sm text-white mb-1">
                  {feature.title}
                </h3>
                <p className="text-xs text-[#666]">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section - PREMIUM REDESIGN */}
      <section id="pricing" className="py-24 px-4 lg:px-8 relative overflow-hidden">
        {/* Ambient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#000000] via-[#0D1218] to-[#000000]"></div>
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-[#00D4FF]/8 rounded-full blur-[150px] pointer-events-none"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#00D9FF]/5 rounded-full blur-[120px] pointer-events-none"></div>
        
        <div className="max-w-[1440px] mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-transparent border border-[#333] rounded-full mb-6">
              <svg className="w-4 h-4 text-[#888]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
              <span className="text-sm font-semibold text-[#B3B3B3]">Planes disponibles</span>
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold mb-4 uppercase tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <span className="text-white">Elige tu</span>{' '}
              <span className="bg-gradient-to-r from-[#00D4FF] via-[#00D4FF] to-[#FFFFFF] bg-clip-text text-transparent">Plan</span>
            </h2>
            <p className="text-[#888] text-lg">Sistema DJ profesional listo para tocar</p>
          </motion.div>
          
          {/* Plans Comparison Table - Desktop - Premium */}
          <div className="hidden lg:block mb-8">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-[#141418] to-[#0D0D12] border border-[#1A1A22] rounded-2xl overflow-hidden"
            >
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#1A1A22]">
                    <th className="p-5 text-left text-sm text-[#888] font-normal">Característica</th>
                    <th className="p-5 text-center bg-[#00D4FF]/10 border-x border-[#00D4FF]/20 relative">
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#00D4FF] to-transparent"></div>
                      <div className="text-lg font-bold text-white">Membership</div>
                      <div className="text-[#00D4FF] text-3xl font-black mt-1">$4.99<span className="text-sm text-[#666] font-normal">/mes</span></div>
                      <div className="inline-block bg-[#00D4FF] text-black text-[10px] font-bold px-2.5 py-0.5 rounded-full mt-1.5">7 DÍAS GRATIS</div>
                    </th>
                    <th className="p-5 text-center">
                      <div className="text-lg font-bold text-white">Starter Pack</div>
                      <div className="text-[#C0C0C0] text-3xl font-black mt-1">$29.99<span className="text-sm text-[#666] font-normal">/único</span></div>
                    </th>
                    <th className="p-5 text-center relative">
                      <div className="text-lg font-bold text-white">Full System</div>
                      <div className="text-[#00D9FF] text-3xl font-black mt-1">$49.99<span className="text-sm text-[#666] font-normal">/único</span></div>
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    { feature: 'Acceso a librería completa', mem: true, start: true, full: true },
                    { feature: 'Descargas ilimitadas', mem: true, start: '200 tracks', full: true },
                    { feature: 'Actualizaciones semanales', mem: true, start: '3 meses', full: '6 meses' },
                    { feature: 'Crates organizados', mem: true, start: true, full: true },
                    { feature: 'BPM y Key analizados', mem: true, start: true, full: true },
                    { feature: 'Soporte prioritario', mem: false, start: false, full: true },
                    { feature: 'Requests personalizados', mem: false, start: false, full: true },
                  ].map((row, i) => (
                    <tr key={row.feature} className="border-b border-[#1A1A22] last:border-0 hover:bg-[#00D4FF]/5 transition-colors">
                      <td className="p-5 text-[#B3B3B3] font-medium">{row.feature}</td>
                      <td className="p-5 text-center bg-[#00D4FF]/5 border-x border-[#00D4FF]/10">
                        {row.mem === true ? <svg className="w-5 h-5 text-[#00D4FF] mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> : 
                         row.mem === false ? <span className="text-[#444]">—</span> : 
                         <span className="text-[#888] text-xs bg-[#1A1A22] px-2 py-1 rounded">{row.mem}</span>}
                      </td>
                      <td className="p-5 text-center">
                        {row.start === true ? <svg className="w-5 h-5 text-[#00D4FF] mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> : 
                         row.start === false ? <span className="text-[#444]">—</span> : 
                         <span className="text-[#888] text-xs bg-[#1A1A22] px-2 py-1 rounded">{row.start}</span>}
                      </td>
                      <td className="p-5 text-center">
                        {row.full === true ? <svg className="w-5 h-5 text-[#00D4FF] mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> : 
                         row.full === false ? <span className="text-[#444]">—</span> : 
                         <span className="text-[#888] text-xs bg-[#1A1A22] px-2 py-1 rounded">{row.full}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
          </div>

          {/* Plan Cards */}
          <div className="grid md:grid-cols-3 gap-5 items-stretch">
            {/* MEMBERSHIP */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-[#141414] border-2 border-[#00D4FF] rounded-2xl p-6 relative shadow-lg shadow-[#00D4FF]/20 flex flex-col"
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-[#00D4FF] text-black text-xs font-bold px-4 py-1 rounded-full">RECOMENDADO</span>
              </div>
              
              <h3 className="text-xl font-bold mb-2 mt-2">WTPL Membership</h3>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-black text-[#00D4FF]">$4.99</span>
                <span className="text-[#666]">/mes</span>
              </div>
              
              <ul className="space-y-2 mb-6 flex-1 text-sm">
                {['Sistema DJ listo para tocar', 'Crates organizados', 'Actualizaciones semanales', 'BPM y Key analizados', 'Cancela cuando quieras'].map((item, idx) => (
                  <li key={item} className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-[#00D4FF] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    <span className="text-[#B3B3B3]">{item}</span>
                  </li>
                ))}
              </ul>
              
              <button
                onClick={handleSubscribe}
                disabled={isProcessing}
                className="w-full bg-[#00D4FF] text-black font-bold py-4 rounded-xl hover:bg-[#00D4FF]/90 transition-colors disabled:opacity-50"
              >
                {isProcessing ? 'Procesando...' : ctaPrimary}
              </button>
              <p className="text-xs text-[#666] text-center mt-2">{ctaSubText}</p>

              {/* Express Checkout — Apple Pay / Google Pay / Link on-site (no Stripe page) */}
              <div className="flex items-center gap-3 my-3">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] uppercase tracking-widest text-[#666]">o paga en 1 tap</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <ExpressCheckoutButton
                onSuccess={() => { window.location.href = '/dashboard'; }}
                onError={(msg) => alert(msg || 'No pudimos completar el pago express — usa el botón principal')}
              />

              <PaymentMethodIcons className="mt-3" showLabel={false} showPayPal={false} />
            </motion.div>
            
            {/* STARTER PACK */}
            <StarterPackCard variant="landing" />
            
            {/* FULL SYSTEM */}
            <FullPackCard variant="landing" />
          </div>
          
          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center items-center gap-6 mt-8 pt-6 border-t border-[#262626]">
            <div className="flex items-center gap-2 text-sm text-[#888]">
              <svg className="w-4 h-4 text-[#00D4FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Garantía 7 días
            </div>
            <div className="flex items-center gap-2 text-sm text-[#888]">
              <svg className="w-4 h-4 text-[#00D4FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Descarga inmediata
            </div>
            <div className="flex items-center gap-2 text-sm text-[#888]">
              <svg className="w-4 h-4 text-[#00D4FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Pago seguro con Stripe · Tarjeta o PayPal
            </div>
          </div>
          
          {/* Social Proof - PROFESSIONAL VERSION */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-10 py-10 bg-gradient-to-br from-[#141414] to-[#0D0D0D] rounded-2xl border border-[#262626] relative overflow-hidden"
          >
            {/* Decorative glows */}
            <div className="absolute top-0 left-1/4 w-48 h-48 bg-[#00D4FF]/10 rounded-full blur-[80px]"></div>
            <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-[#00D4FF]/10 rounded-full blur-[80px]"></div>
            
            <div className="text-center relative z-10 px-6">
              {/* Main Message - Professional without user count */}
              <motion.div 
                initial={{ scale: 0.9 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                className="mb-8"
              >
                <div className="flex items-center justify-center gap-3 mb-4">
                  <h3 className="text-2xl lg:text-3xl font-black text-white">
                    Música Latina Profesional
                  </h3>
                </div>
                <p className="text-lg text-[#B3B3B3] max-w-2xl mx-auto">
                  Curaduría real para <span className="text-[#00D4FF] font-semibold">DJs de eventos y club</span>. 
                  Pensado para profesionales que exigen calidad.
                </p>
              </motion.div>
              
              {/* Locations */}
              <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
                {['Houston', 'Miami', 'México', 'Puerto Rico', 'República Dominicana'].map((location) => (
                  <span key={location} className="text-sm px-4 py-2 bg-[#1A1A1A] rounded-full text-[#888] border border-[#262626]">
                    {location}
                  </span>
                ))}
              </div>
              
              {/* Stats Grid - Enhanced - No user count */}
              <div className="flex items-center justify-center gap-6 lg:gap-10 flex-wrap">
                <motion.div 
                  whileHover={{ scale: 1.05, borderColor: '#00D4FF' }}
                  className="text-center px-8 py-5 bg-[#000000] rounded-2xl border-2 border-[#00D4FF]/30 transition-all"
                >
                  <p className="text-4xl lg:text-5xl font-black text-[#00D4FF]">{stats.tracks.toLocaleString()}+</p>
                  <p className="text-sm text-[#888] font-medium mt-1">Tracks disponibles</p>
                </motion.div>
                <motion.div 
                  whileHover={{ scale: 1.05, borderColor: '#00D4FF' }}
                  className="text-center px-8 py-5 bg-[#000000] rounded-2xl border-2 border-[#00D4FF]/30 transition-all"
                >
                  <p className="text-4xl lg:text-5xl font-black text-[#00D4FF]">100+</p>
                  <p className="text-sm text-[#888] font-medium mt-1">Nuevos cada semana</p>
                </motion.div>
                <motion.div 
                  whileHover={{ scale: 1.05, borderColor: '#00D4FF' }}
                  className="text-center px-8 py-5 bg-[#000000] rounded-2xl border-2 border-[#00D4FF]/30 transition-all"
                >
                  <p className="text-4xl lg:text-5xl font-black text-[#00D4FF]">{stats.genres}</p>
                  <p className="text-sm text-[#888] font-medium mt-1">Géneros latinos</p>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center uppercase tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Preguntas Frecuentes
          </h2>
          
          <div className="grid md:grid-cols-2 gap-3">
            {faqs.map((faq, i) => (
              <div key={faq.question || `faq-${i}`} className="bg-[#141414] border border-[#262626] rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleFAQ(i)}
                  className="w-full px-5 py-4 flex justify-between items-center text-left hover:bg-[#1A1A1A] transition-colors"
                >
                  <span className="font-medium text-sm">{faq.question}</span>
                  <span className={`text-[#666] transition-transform ${activeFAQ === i ? 'rotate-180' : ''}`}>▼</span>
                </button>
                <AnimatePresence>
                  {activeFAQ === i && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-5 pb-4 text-sm text-[#B3B3B3] overflow-hidden"
                    >
                      {faq.answer}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 lg:px-8 bg-gradient-to-b from-[#0D0D0D] to-[#000000]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4 uppercase" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Únete a WTPL Hoy
          </h2>
          <p className="text-[#B3B3B3] mb-8">Música latina profesional para DJs exigentes</p>
          <motion.button
            onClick={handleSubscribe}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-[#00D4FF] text-black font-bold px-10 py-4 rounded-full hover:bg-[#00D4FF]/90 text-lg"
          >
            {ctaPrimary}
          </motion.button>
          <p className="text-[#666] text-sm mt-3">{ctaSubText}</p>
          <PaymentMethodIcons className="mt-4" showPayPal={false} />
        </div>
      </section>

      {/* Footer - IMPROVED */}
      <footer className="bg-[#000000] border-t border-[#1A1A1A] py-12 px-4 lg:px-8">
        <div className="max-w-[1440px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-8">
            {/* Logo */}
            <div className="col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo-icon.png" alt="WTPL" className="h-9 w-9 rounded-lg" />
                <span className="font-bold text-xl" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>WTPL</span>
              </div>
              <p className="text-sm text-[#666] mb-4">DJ Pool Latino Profesional</p>
              <p className="text-xs text-[#444]">El pool de música latina #1 para DJs profesionales.</p>
            </div>

            {/* Navigation */}
            <div>
              <h4 className="font-bold mb-4 text-xs text-white tracking-[0.2em] uppercase">Navegación</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="text-[#888] hover:text-[#00D4FF] transition-colors">Inicio</a></li>
                <li><a href="#biblioteca" className="text-[#888] hover:text-[#00D4FF] transition-colors">Biblioteca</a></li>
                <li><a href="#generos" className="text-[#888] hover:text-[#00D4FF] transition-colors">Géneros</a></li>
                <li><a href="#pricing" className="text-[#888] hover:text-[#00D4FF] transition-colors">Planes</a></li>
                <li><a href="#faq" className="text-[#888] hover:text-[#00D4FF] transition-colors">FAQ</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-bold mb-4 text-xs text-white tracking-[0.2em] uppercase">Soporte</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#faq" className="text-[#888] hover:text-[#00D4FF] transition-colors">Ayuda</a></li>
                <li><a href="mailto:planetlyo@gmail.com" className="text-[#888] hover:text-[#00D4FF] transition-colors">Contacto</a></li>
                <li><a href="mailto:planetlyo@gmail.com" className="text-[#888] hover:text-[#00D4FF] transition-colors">Soporte</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-bold mb-4 text-xs text-white tracking-[0.2em] uppercase">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/terms" className="text-[#888] hover:text-[#00D4FF] transition-colors">Términos</a></li>
                <li><a href="/privacy" className="text-[#888] hover:text-[#00D4FF] transition-colors">Privacidad</a></li>
                <li><a href="/dmca" className="text-[#888] hover:text-[#00D4FF] transition-colors">DMCA</a></li>
              </ul>
            </div>

            {/* Social */}
            <div>
              <h4 className="font-bold mb-4 text-xs text-white tracking-[0.2em] uppercase">Síguenos</h4>
              <a 
                href="https://instagram.com/dj_lyo96" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center gap-2 text-[#888] hover:text-[#E1306C] transition-colors text-sm"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                Instagram
              </a>
            </div>
          </div>

          <div className="border-t border-[#1A1A1A] pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-[#666]">
              © {new Date().getFullYear()} WTPL - Welcome To Planet LYO. Todos los derechos reservados.
            </p>
            <div className="flex items-center gap-4 text-xs text-[#444]">
              <span>Houston, TX</span>
              <span>•</span>
              <span>Made for DJs, by a DJ</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Retention modal — shown when returning from an abandoned Stripe checkout */}
      <CheckoutRecoveryModal
        open={showRecoveryModal}
        onClose={() => setShowRecoveryModal(false)}
        onResume={() => {
          setShowRecoveryModal(false);
          handleSubscribe();
        }}
        isProcessing={isProcessing}
        isReturningSubscriber={hasPreviousSubscription}
      />
    </div>
  );
};

export default LandingPage;
